import { useNavigate } from "react-router-dom";
import { AUTH_PROVIDER } from "../lib/auth-provider";
import { SIGNUP_ENABLED, SIGNUP_CLOSED, SIGNUP_CLOSED_MESSAGE } from "../lib/signup-policy";


const authModule = await (
  AUTH_PROVIDER === "null"
    ? import('./null-user.js')
    : AUTH_PROVIDER === "better-auth"
      ? import('./better-auth-user.js')
      : import('./firebase-user.js')
);

export function useUser() {
  // we don't store these things in the DB, so have to grab from FB
  return { ...(authModule.getCurrentUser()), logout: async () => authModule.logOut(), reload: async () => authModule.reload() };
}


export function useCreateUser() {
  const navigate = useNavigate();
  return {
    createEmail: async ({ email, password }) => {
      // Belt and braces. /signup already renders the referral page rather than
      // a form when registration is closed, so nothing should reach here — but
      // this is the single choke point every provider's signUp goes through,
      // and it is the one worth being sure of.
      if (!SIGNUP_ENABLED) throw new Error(SIGNUP_CLOSED_MESSAGE);
      await authModule.signUp(email, password);
      navigate("/login");
    },
    sendEmail: async () => {
      await authModule.sendVerificationEmail();
      navigate("/login");
    }

  };
}

// Owns both destinations so that "you cannot register here" lands on the
// referral page instead of home. Every other failure still surfaces to the
// caller's error toast.
async function signInOrRefer(navigate, signIn) {
  try {
    await signIn();
    navigate("/");
  }
  catch (err) {
    if (err?.code !== SIGNUP_CLOSED) throw err;
    navigate("/signup");
  }
}

export function useLogin() {
  const navigate = useNavigate();
  return {
    loginEmail: async ({ email, password }) => {
      await authModule.logIn(email, password);
      navigate("/");
    },
    passwordReset: async ({ email }) => {
      await authModule.sendResetEmail(email);
      navigate("/login");
    },
    resetPassword: async ({ token, newPassword }) => {
      await authModule.resetPassword({ token, newPassword });
      navigate("/login");
    },
    // A Google/GitHub popup is both the sign-in and the sign-up path, so a
    // stranger clicking "Login in with Google" is really registering. The
    // provider undoes the account Firebase just made and throws; an error toast
    // would be a dead end for someone who simply wants the service, so send
    // them where they can actually get one.
    loginGoogle: async () =>
      signInOrRefer(navigate, () => authModule.signInWithGoogle()),
    loginGithub: async () =>
      signInOrRefer(navigate, () => authModule.signInWithProvider('githubProvider'))
  };
}

